<template>
  <el-dialog v-model="dialogVisible" :width="'1100px'" :before-close="handleClose" class="collection-editing-dialog" >
    <template #header>
      <h3 class="dialog-title">{{ !isEdit ? `New ${whichTypeTitle}` : `Edit ${whichTypeTitle}` }}</h3>
    </template>

    <div class="content-box" v-loading="loading">
      <!-- LEFT: Cover / Title / Description -->
      <Scrollbar class="left-col">
        <div class="form-row">
          <div class="cover-label">
            <label class="field-label">Collection cover<span class="required">*</span></label>
            <a class="link" v-if="ruleForm.img.src" @click.prevent="is_show_cropper = true">Replace image</a>
          </div>

          <div class="cover-area" @click="is_show_cropper = true">
            <div class="cover-preview">
              <img v-if="ruleForm.img.src" :src="ruleForm.img.src" alt="cover" />
              <div class="img-box-empty" v-else>
                <el-button>Upload image</el-button>
              </div>
            </div>
          </div>
          <p class="red-text" v-if="ruleForm.img_err">{{ ruleForm.img_err }}</p>
        </div>

        <div class="form-row">
          <label class="field-label">Collection title<span class="required">*</span></label>
          <el-input :class="[ruleForm.title_err && 'err-input']" v-model="ruleForm.title" :placeholder="`Enter ${whichType} title`" maxlength="80" show-word-limit />
          <p class="red-text" v-if="ruleForm.title_err">{{ ruleForm.title_err }}</p>
        </div>

        <div class="form-row">
          <label class="field-label">Description (optional)</label>
          <el-input type="textarea" v-model="ruleForm.description"
            :placeholder="`Tell us something about your ${whichType}...`" :rows="4" maxlength="250" show-word-limit />
        </div>

        <p class="from-row one-flex red-text" v-if="(data?.status == STATUS_CODE.Disabled || data?.status == STATUS_CODE.Active) && needToAddProduct">Collection without products will stay disabled</p>
        <div class="from-row one-flex" v-if="data?.status == STATUS_CODE.Disabled || data?.status == STATUS_CODE.Active">
          <label class="field-label">Status</label>
          <div class="status-text">{{ ruleForm.set_active ? 'Active' : 'Disabled' }}</div>
          <Switch :checked="ruleForm.set_active" @click="handleStatus" />
        </div>
      </Scrollbar >
      <!-- RIGHT: Selected Creators / Permissions / Disclosures / Schedule -->
      <Scrollbar class="right-col">
        <div class="carded">
          <div class="carded-header">
            <div class="carded-title">Recipients</div>
          </div>

          <div class="post-to">
            <div class="post-to-fake-input">
              <span v-for="(item, index) in ruleForm.post_to.data" :key="index">
                {{ item.tag ? `${item.tag} (${item.creator_count})` : item.name }}
              </span>
            </div>
            <el-button @click="openPostTo();">Edit</el-button>
          </div>
        </div>

        <!-- Creator Permissions -->
        <div class="carded">
          <div class="carded-header">
            <div class="carded-title">Creator permissions</div>
          </div>

          <div class="carded-body permissions">
            <div v-for="perm in ruleForm.permission" :key="perm.key" class="perm-row" v-show="perm.show">
              <Switch :checked="perm.checked" @click="perm.checked = !perm.checked" />
              <div class="perm-label">{{ perm.label }}</div>
            </div>
          </div>
        </div>

        <!-- Disclosures -->
        <div class="carded">
          <div class="carded-header">
            <div class="carded-title">Disclosures</div>
          </div>
          <div class="carded-body">
            <div class="disclosures">
              <Switch :checked="ruleForm.disclosures"
              @click="ruleForm.disclosures = !ruleForm.disclosures" />
              <span>When selected, this collection will show a label on creators' storefronts to
                shoppers to indicate it is part of a brand promotion.</span>
            </div>
            <div class="dis-input-box" v-if="ruleForm.disclosures">
              <el-input :class="['dis-input',ruleForm.dis_input_err && 'err-input']" v-model="ruleForm.dis_val" maxlength="30" show-word-limit/>
              <p class="red-text" v-if="ruleForm.dis_input_err">{{ ruleForm.dis_input_err }}</p>
            </div>
            
            <!-- <Checkbox :label_text="'Disclaim some products or collections are sponsored'"
              :checked="ruleForm.disclosures" @click="() => (ruleForm.disclosures = !ruleForm.disclosures)" /> -->
          </div>
        </div>

        <!-- Schedule -->
        <div class="carded">
          <div class="carded-header">
            <div class="carded-title">Schedule (UTC)</div>
          </div>

          <ScheduleCard
            :publishEnabled="ruleForm.publishEnabled"
            :publishDate="ruleForm.publishDate"
            :showPublishSwitch="true"
            :publishedAtText="data?.published_at && !ruleForm.publishEnabled ? formatDate(data?.published_at) : ''"
            :disableEnabled="ruleForm.disableEnabled"
            :disableDate="ruleForm.disabledDate"
            :showDisableSwitch="true"
            :disabledAtText="!data?.disable_datetime && !ruleForm.disableEnabled ? '' : ''"
            @toggle:publish="ruleForm.publishEnabled = !ruleForm.publishEnabled"
            @toggle:disable="ruleForm.disableEnabled = !ruleForm.disableEnabled"
            @update:publishDate="handlePublishDate"
            @update:publishTime="handlePublishTime"
            @update:disableDate="handleDisableDate"
            @update:disableTime="handleDisableTime"
          />
        </div>
        <div class="carded-right">
          <div class="w-100" @click="DeleteCollection" v-if="isEdit && data?.status === STATUS_CODE.Draft">
            <el-button class="red-text" text>Delete</el-button>
          </div>
        </div>
      </Scrollbar>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose" v-if="isEdit">Cancel</el-button>
        <el-button type="primary" :disabled="!canCreate" @click="submitCreate" v-if="isEdit">Save</el-button>
        <el-button type="primary" :disabled="!canAddProduct" @click="submitCreate('add')" v-if="!isEdit">Next:add products</el-button>
      </div>
    </template>
  </el-dialog>

  <!-- handle image -->
  <el-dialog v-model="is_show_cropper" title="Thumbnail" width="1000" :key="handle_clear_key">
    <UploadImage @saveAvatar="saveImage" :aspectRatio="1" :maxImage="5" />
  </el-dialog>

  <el-dialog v-model="checkSaveDialog" width="300" :close-on-click-modal="false" :show-close="false"
    class="save-alert-dialog">
    <div class="content">Since no publish date has been selected, this item will be saved as a draft. Would you like to
      proceed?</div>
    <div slot="footer" class="dialog-footer">
      <el-button @click="checkSaveDialog = false">Cancel</el-button>
      <el-button type="primary" @click="submitCreateGo">Confirm</el-button>
    </div>
  </el-dialog>

  <confirm-dialog ref="confirm_date_el" :data="confirm_date" />

</template>

<script setup>

import { ref, getCurrentInstance, onMounted, computed, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { ElDialog, ElButton, ElInput } from 'element-plus';
import { useNotification } from '@/components/ui/notification';
import Switch from '@/components/ui/Switch.vue';
import ScheduleCard from '@/components/storefront/ScheduleCard.vue';
import UploadImage from '@/components/ui/UploadImage.vue';
import { useStorefrontFunction } from '@/components/storefront/StorefrontFunction.js';
import Scrollbar from '@/components/ui/Scrollbar.vue';
import ConfirmDialog from '@/components/storefront/collectionDetail/ConfirmDialog.vue';
import moment from 'moment';

const store = useStore();
const app = getCurrentInstance();
const ajax = app.appContext.config.globalProperties.$ajax;
const { openNotBottom } = useNotification();
const { openRecipientEditDialog, STATUS_CODE } = useStorefrontFunction();
const is_show_cropper = ref(false);
const handle_clear_key = ref(0);
const is_need_refresh = ref(false);
const loading = ref(false);
const ruleForm = ref({
  img: {
    src: null,
    data: null
  },
  img_err: '',
  title: "",
  title_err:'',
  description: "",
  click_all: true,
  post_to: { type: 'all', data: [{ name: 'All' }] },
  recipient_creators: [],
  recipient_groups: [],
  permission: [
    {
      key: "feature",
      label: "Feature on creators’ storefront homepages",
      checked: false,
      show: true
    },
    {
      key: "remove",
      label: "Allow creators to remove this collection from their storefronts",
      checked: false,
      show: true
    },
    {
      key: "addRemove",
      label:
        "Allow creators to add and remove products from this collection on their storefront",
      checked: false,
      show: true
    },
  ],
  disclosures: false,
  dis_val:'Promoted',
  dis_input_err:'',
  publishEnabled: false,
  disableEnabled: false,
  publishDate: {
    date: null,
    time: null,
    val: null
  },
  disabledDate: {
    date: null,
    time: null,
    val: null
  },
  notifyCreators: false,
  status: true,
  is_draft: true,
});
const isEdit = ref(false);
//+++++++++++++++++++++++++++++++
const eventBus = app.appContext.config.globalProperties.EventBus;
const dialogVisible = ref(false);
const data = ref(null);
const whichType = ref('post'); // post, collection
const checkSaveDialog = ref(false);
const confirm_date_el = ref(null);
const confirm_date = ref({
  title: '',
  description: '',
  action: () => {}
});

const whichTypeTitle = computed(() => {
  if (whichType.value === 'post') {
    return 'Post';
  } else if (whichType.value === 'collection') {
    return 'Collection';
  } else {
    return '';
  }
});

const canCreate = computed(() => {
  const val = ruleForm.value;

  const hasAny =
    !!(val.img?.src || val.img?.data || val.title );

  const hasAll =
    !!(val.img?.src || val.img?.data) &&
    !!val.title;

  return val.is_draft ? hasAny : hasAll;
});

const canAddProduct= computed(() => {
  const val = ruleForm.value;
  const hasAll =
    (val.img?.src || val.img?.data) ||
    val.title
  return  hasAll;
});

const needToAddProduct = computed(()=>{
  let val = ruleForm.value;
  return val.status && (!isEdit.value || !data.value.product_count);
});

// const disableMinDate= computed(()=>{
//   if(ruleForm.value.publishDate.val){
//     return new Date(ruleForm.value.publishDate.val);
//   }else{
//     return new Date();
//   }
// });
//++++++++++++++++++++++++++++++++++++++
//+++++++++++++++++++++++++++++++++++++
const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return moment(dateStr).format('YYYY-MM-DD hh:mm:ss A');
};
const open = (getdata) => {
  handle_clear_key.value += 1;
  isEdit.value = getdata.row?.id ? true : false;
  is_need_refresh.value = getdata.type === 'is-need-refresh' ? true : false;
  data.value = getdata.row;
  whichType.value = getdata.which
  dialogVisible.value = true;
  handleData(data.value);
};

const handleClose = () => {
  dialogVisible.value = false;
};

const publishNotYet = (val) => {
  if (!val) return false; 

  const now = moment.utc();
  const target = moment.utc(val);

  return target >= now;
};

const handleData = (data) => {
  console.log("data", data)
  ruleForm.value.id = data?.id ? data.id : null;
  ruleForm.value.title = data?.title ? data.title : '';
  ruleForm.value.description = data?.description ? data.description.replace(/\r\n/g, '\n') : '';
  ruleForm.value.post_to_count = data && data.recipient_creator_count ? data.recipient_creator_count : 0;
  const RuleformatDateTime = (datetime) => {
    if (!datetime) {
      return { date: null, time: null, val: null };
    }
    const m = moment(datetime);
    return {
      date: m.format('MMM DD, YYYY'),
      time: m.format('h:mm A'),
      val: m.format('YYYY-MM-DD HH:mm:ss'),
    };
  };

  ruleForm.value.publishDate = data?.is_schedule ? RuleformatDateTime(data && data.publish_datetime) : RuleformatDateTime(null);
  ruleForm.value.disabledDate = RuleformatDateTime(data && data.disable_datetime);
  ruleForm.value.img = {
    src: data && data.cover_image_url ? data.cover_image_url : null,
    data: null,
  };

  let creator_ids = data && Array.isArray(data.recipient_creators)
    ? data.recipient_creators
    : [];

  let group_ids = data && Array.isArray(data.recipient_groups)
    ? data.recipient_groups
    : [];

  if ((data && data.apply_to_all_creators) || !data) {
    ruleForm.value.post_to = { type: 'all', data: [{ name: 'All' }] };
  } else {
    ruleForm.value.post_to = { type: 'pick', data: [...creator_ids, ...group_ids] };
  }
  ruleForm.value.click_all = (data && data.apply_to_all_creators) || !data ? true : false;
  ruleForm.value.recipient_creators = data && data.recipient_creators ? data.recipient_creators : [];
  ruleForm.value.recipient_groups = data && data.recipient_groups ? data.recipient_groups : [];
  ruleForm.value.permission[0].checked = data && data.is_feature ? true : false;
  ruleForm.value.permission[1].checked = data && data.allow_creator_remove ? true : false;
  ruleForm.value.permission[2].checked = data && data.allow_creator_edit ? true : false;
  if(data?.status === STATUS_CODE.Disabled){
    ruleForm.value.permission[0].show = false;
  }else{
    ruleForm.value.permission[0].show = true;
  }
  //ruleForm.value.permission[3].checked = data && data.import_product_to_storefront ? true : false;
  ruleForm.value.disclosures = data && data.is_sponsored_disclosure_enabled ? true : false;
  ruleForm.value.dis_val = data && data.sponsored_disclosure_name ? data.sponsored_disclosure_name.replace(/\r\n/g, '\n') : 'Promoted';
  ruleForm.value.publishEnabled = data && data.is_schedule ? true : false;
  ruleForm.value.disableEnabled = data && data.disable_datetime ? true : false;
  ruleForm.value.is_happen_publish = publishNotYet(data && data.publish_datetime);
  ruleForm.value.set_active = data && data.status === STATUS_CODE.Disabled ? false : true;
  ruleForm.value.notifyCreators = data && data.notify_creator_on_publish ? true : false;
  ruleForm.value.is_draft = data && data.status === STATUS_CODE.Draft ? true : !data? true :false;
  ruleForm.value.title_err = '';
  ruleForm.value.img_err = '';
};

const saveImage = (data) => {
  ruleForm.value.img = data;
  is_show_cropper.value = false;
};

const handleDateTime = (field) => {
  if (field['date'] !== 'Invalid date') {
    if (field['time'] !== 'Invalid date' && field['time']) {
      field['val'] = moment(`${field['date']} ${field['time']}`).format('YYYY-MM-DD HH:mm:ss');
    } else {
      field['time'] = '00:00:00';
      field['val'] = moment(`${field['date']} ${field['time']}`).format('YYYY-MM-DD HH:mm:ss');
    }

  } else {
    field['val'] = '';
  }
};
const handleStatus = () => {
  if(needToAddProduct.value) return;
  ruleForm.value.set_active = !ruleForm.value.set_active; 
  if(ruleForm.value.set_active){
    ruleForm.value.permission[0].show = true;
  }else{
    ruleForm.value.permission[0].show = false;
    ruleForm.value.permission[0].checked = false;
  }
};

//schedule
const handlePublishDate = (val) => {
  ruleForm.value.publishDate.date = moment(val.val, 'MM/DD/YYYY').format('YYYY-MM-DD');
  // handleDateTime(ruleForm.value.publishDate);
  if(ruleForm.value.disableEnabled){
    let pubDate=new Date( ruleForm.value.publishDate.val);
    let disDate=new Date( ruleForm.value.disabledDate.val);
    if(pubDate>disDate){
      ruleForm.value.disabledDate.date='';
      ruleForm.value.disabledDate.time='';
      ruleForm.value.disabledDate.val='';
    }
  }
};

const handlePublishTime = (t) => {
  ruleForm.value.publishDate.time = moment(t.val, 'h:mm A').format('HH:mm:ss');
  handleDateTime(ruleForm.value.publishDate);
};

const handleDisableDate = (d) => {
  ruleForm.value.disabledDate.date = moment(d.val, 'MM/DD/YYYY').format('YYYY-MM-DD');
  // handleDateTime(ruleForm.value.disabledDate);
};
const handleDisableTime = (t) => {
  ruleForm.value.disabledDate.time = moment(t.val, 'h:mm A').format('HH:mm:ss');
  handleDateTime(ruleForm.value.disabledDate);
};

const handleFORAPIData = () => {
  let val = ruleForm.value;
  let pubDate = val.publishEnabled && publishNotYet(val.publishDate.val) ? val.publishDate.val : moment.utc().format('YYYY-MM-DD HH:mm:ss');
  
  // console.log("pubDate for api", pubDate, moment.utc().format('YYYY-MM-DD HH:mm:ss'));
  // return;
  const params = {
    title: val.title,
    description: val.description,
    is_feature: val.permission.find(p => p.key === 'feature').checked ? 1 : 0,
    is_schedule: val.publishEnabled ? 1 : 0,
    allow_creator_remove: val.permission.find(p => p.key === 'remove').checked ? 1 : 0,
    allow_creator_edit: val.permission.find(p => p.key === 'addRemove').checked ? 1 : 0,
    notify_creator_on_publish: val.notifyCreators ? 1 : 0,
    //import_product_to_storefront: val.permission.find(p => p.key === 'autoAdd').checked ? 1 : 0,
    is_sponsored_disclosure_enabled: val.disclosures ? 1 : 0,
    apply_to_all_creators: val.click_all ? 1 : 0,
    publish_datetime: val.publishEnabled ? pubDate : undefined,
    disable_datetime: val.disableEnabled ? val.disabledDate.val : undefined,
    sponsored_disclosure_name: val.dis_val,
  };

  if (val.img.data) {
    params.cover_image_url = val.img.data;
  }

  // (for creator tag)
  if (!val.click_all) {
    params.anchor_creator_ids = val.recipient_creators.map(item => item.recipient_id);
    params.tag_ids = val.recipient_groups.map(item => item.id);
  }

  if (isEdit.value) {
    params.curation_collection_id = val.id;
  }

  let formData = new FormData();

  for (const key in params) {
    if (params[key] === undefined || params[key] === null) continue;

    if (Array.isArray(params[key])) {
      params[key].forEach(value => {
        formData.append(`${key}[]`, value);
      });
    } else {
      formData.append(key, params[key]);
    }
  }
  return formData;
};


const openAddProduct=(isNew)=>{
  dialogVisible.value=false;
  eventBus.emit('open-collection-detail', {
    content_id: data.value.id,
    is_edit: false,
    row: data.value, //mp new
    need_open_pop:true
  });
  window.localStorage.setItem('storefront_collection_detail_row', JSON.stringify(data.value));
  const newUrl = `${location.pathname}?id=${data.value.id}${isNew ? '&isNew=true' : ''}`;
  history.replaceState(null, '', newUrl); 
};

const callPublishApi = async () => {
  let val=ruleForm.value;
  let checkStartDateIsSchedule = val.publishEnabled && publishNotYet(val.publishDate.val);
  // console.log("checkStartDateIsSchedule",checkStartDateIsSchedule)
  if ((data.value?.status === STATUS_CODE.Disabled && val.set_active)||
    (data.value?.status === STATUS_CODE.Active && val.set_active && !checkStartDateIsSchedule)  ) {
    const pub_params_data = {
      'curation_collection_ids[]': data.value.id,
    };

    try {
      const response = await ajax.post(
        '/storefront-curation/publish-curation-collection',
        pub_params_data,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      // Trigger queue update event
      eventBus.emit('ready-queue-update');

      return response.data; 
    } catch (error) {
      throw error; 
    }
  }
};

const callDisabledApi = async () => {
  if (data.value?.status === STATUS_CODE.Active && !ruleForm.value.set_active) {
    const pub_params_data = {
      'curation_collection_ids[]': data.value.id,
    };

    try {
      const response = await ajax.post(
        '/storefront-curation/disable-curation-collection',
        pub_params_data,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      // Trigger queue update event
      const jobId = response?.data?.data?.job_id || response?.data?.job_id || `collection-disable-${Date.now()}`;
      eventBus.emit('ready-queue-update', { jobId });

      return response.data; 
    } catch (error) {
      throw error;
    }
  }
};

const submitCreate = async (is_add) => {
  // if(loading.value){
  //   return;
  // }
  // loading.value = true;

  let val = ruleForm.value;
  ruleForm.value.title_err = val.title ? '' : 'Cannot leave blank.';
  ruleForm.value.img_err = (val.img.src || val.img.data) ? '' : 'Cannot leave blank.';
  ruleForm.value.dis_input_err= !val.dis_val ? 'Cannot leave blank.': '';
  if (ruleForm.value.title_err || ruleForm.value.img_err || val.description.length > 250 || ruleForm.value.dis_input_err) {
    loading.value = false;
    return;
  }

  // Validate scheduled times before proceeding
  const currentUtcTime = moment.utc();
  const currentTimestamp = currentUtcTime.valueOf();

  // Check if publish date is in the past
  if (val.publishEnabled && val.publishDate?.val) {
    const scheduledTime = moment.utc(val.publishDate.val);
    const scheduledTimestamp = scheduledTime.valueOf();
    
    if (scheduledTimestamp < currentTimestamp) {
      // Show confirmation dialog for past scheduled time
      const scheduledTimeStr = scheduledTime.format('MMM D, YYYY h:mm A');
      const currentTimeStr = currentUtcTime.format('MMM D, YYYY h:mm A');
      
      confirm_date.value = {
        title: 'Would you like to publish now or reschedule?',
        description: `Your scheduled publish time of ${scheduledTimeStr} UTC has passed (current time: ${currentTimeStr} UTC).`,
        cancel_button_text: 'No, reschedule it',
        confirm_button_text: 'Publish now',
        action: () => {
          proceedWithSubmit(is_add);
        }
      };
      
      confirm_date_el.value.open();
      loading.value = false;
      return;
    }
  }

  // Check if disable date is in the past
  if (val.disableEnabled && val.disabledDate?.val) {
    const scheduledEndTime = moment.utc(val.disabledDate.val);
    const scheduledEndTimestamp = scheduledEndTime.valueOf();
    
    if (scheduledEndTimestamp < currentTimestamp) {
      // Show confirmation dialog for past scheduled end time
      const scheduledEndTimeStr = scheduledEndTime.format('MMM D, YYYY h:mm A');
      const currentTimeStr = currentUtcTime.format('MMM D, YYYY h:mm A');
      
      confirm_date.value = {
        title: 'Scheduled end time has passed',
        description: `Your scheduled end time of ${scheduledEndTimeStr} UTC has passed (current time: ${currentTimeStr} UTC). Do you want to proceed? The collection will be disabled immediately.`,
        cancel_button_text: 'No, reschedule it',
        confirm_button_text: 'Proceed',
        action: () => {
          proceedWithSubmit(is_add);
        }
      };
      
      confirm_date_el.value.open();
      loading.value = false;
      return;
    }
  }

  // Proceed normally if no past schedule conflict
  await proceedWithSubmit(is_add);
};

const proceedWithSubmit = async (is_add) => {
  if(loading.value){
    return;
  }
  loading.value = true;
  // console.log('proceedWithSubmit called');

  let val = ruleForm.value;

  // Check if collection has products
  const hasProducts = isEdit.value && data.value?.product_count > 0;

  // Only set publish datetime if collection has products
  if (!val.is_draft && hasProducts) {
    if ((!val.publishEnabled || !val.publishDate.date || !val.publishDate.time)) {
      ruleForm.value.publishDate.val = moment.utc().format('YYYY-MM-DD HH:mm:ss');
    }
  }

  is_add = is_add === 'add' ? true : false;
  let form = handleFORAPIData();

  // console.log('form data prepared for submission:', form);
  /*
    Status logic summary:

    Draft:
      - No manual status change.
      - If time is updated → remain Draft until manually published.

    Scheduled:
      - No manual status change.
      - If time is updated (future only) → remain Scheduled until manually published.

    Disabled:
      - Change to Active → trigger Publish API.
      - If time is updated → remain Disabled until manually published.

    Active:
      - Change to Disabled → trigger Disable API.
      - If time is updated → auto-update status to Scheduled.
      - Any content update should trigger Publish API,
        except when "Start Date" is enabled and set to a future time.
    */
  let api = isEdit.value ? '/storefront-curation/update-curation-collection' : '/storefront-curation/add-curation-collection';
  try {
    const res = await ajax.post(api, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    const hasProducts = isEdit.value && data.value?.product_count > 0;

    //orgin in disabled, is turn to active and has products
    if (data.value?.status === STATUS_CODE.Disabled && val.set_active && hasProducts) {

      openNotBottom('Collection info saved', 'success');
      if(hasProducts) await callPublishApi();
      await callDisabledApi();
      if(!needToAddProduct.value) dialogVisible.value = false;
      eventBus.emit('refresh-storefront-collection-table');

    } else {
      eventBus.emit('set-not-empty');
      openNotBottom('Collection info saved', 'success');
      // Only call publish API if collection has products
      if(hasProducts) await callPublishApi();
      await callDisabledApi();
      if(!needToAddProduct.value) dialogVisible.value = false;
      eventBus.emit('refresh-storefront-collection-table');
      if(is_add){
        data.value = res.data.data.data[0];
        openAddProduct(!isEdit.value);
      }else{
        dialogVisible.value = false;
      }
    }

    if (is_need_refresh.value) {
      eventBus.emit('refresh-product-collection-list');
    }
    loading.value = false;
  } catch (error) {
    console.log('err', error);
    openNotBottom(`Collection info save error. `, 'error');
    loading.value = false;
  }

};

const submitCreateGo = async () => {
  ruleForm.value.is_draft = true;
  checkSaveDialog.value = false;
  await submitCreate();
};

const openPostTo = async () => {
  const result = await openRecipientEditDialog(ruleForm.value);
  ruleForm.value = { ...ruleForm.value, ...result };
};

const DeleteCollection = () => {
  eventBus.emit('open-storefront-collection-handle-dialog', {
    type: 'Delete',
    data: [data.value]
  });
};

//+++++++++++++++++++++++++++++++++++++++++++++++++++++
onMounted(() => {
  eventBus.on('open-collection-edit-dialog', (data) => {
    open(data);
  });

});

onBeforeUnmount(() => {
  eventBus.off('open-collection-edit-dialog');

});

</script>

<style lang="scss">
.mx-datepicker-popup {
  z-index: 999900;
}

.collection-editing-dialog {
  max-height: 78vh;

  .red-text{
    color: var(--red);
  }

  .content-box {
    display: grid;
    gap: 24px;
    padding: 20px 20px 12px;
    grid-template-columns: 300px auto;

    .left-col {
      display: grid;
      gap: 16px;
      grid-template-rows: auto auto 1fr;
      max-height: calc(70vh - 105px);
    }

    .form-row {
      display: flex;
      flex-direction: column;
      gap: 2px;

      label {
        font: var(--medium-16);
        color: var(--black-75);
      }

      .el-input,
      .el-select,
      .el-switch {
        width: 100%;
      }
    }

    .link {
      cursor: pointer;
    }

    .required {
      color: var(--red);
      &::after {
        display: none;
      }
    }

    .cover-label {
      display: flex;

      label {
        flex: 1;
      }
    }

    .cover-area {
      width: 100%;
      height: 280px;
      border: 1px solid var(--border);
      border-radius: 6px;
      background: var(--bg-03);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      overflow: hidden;

      .cover-preview {
        width: 100%;
        height: 100%;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .img-box-empty {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
    }

    .creator-select {
      .el-select {
        width: 300px;
      }
    }

    .right-col {
      display: grid;
      gap: 16px;
      max-height: calc(70vh - 105px);

      .carded {
        border-radius: 16px;
        background-color: #fafafa;
        padding: 16px;

        .carded-header {
          display: flex;
          align-items: center;

          .carded-title {
            font: var(--medium-16);
            color: black;
            flex: 1;
          }
        }

        .carded-body{
          .disclosures{
            display: flex;
            gap:8px;
            margin-top: 8px;

            .ui-switch{
              margin-top: 2px;
            }
          }

          .dis-input-box{
            padding-left: 30px;
            margin-top: 4px;

            input{
              font-size: 12px;
            }
          }
        }

        .selected-info {
          display: flex;

          .select-row {
            flex: 1;
          }
        }

        .permissions {
          margin-top: 16px;

          .perm-row {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;
          }
        }
      }


      .post-to {
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 4px;

        .post-to-fake-input {
          display: inline-flex;
          flex-grow: 1;
          align-items: center;
          justify-content: flex-start;
          padding: 4px 11px;
          background-color: var(--el-input-bg-color, var(--el-fill-color-blank));
          background-image: none;
          border-radius: var(--el-input-border-radius, var(--el-border-radius-base));
          transition: var(--el-transition-box-shadow);
          transform: translate3d(0, 0, 0);
          box-shadow: 0 0 0 1px var(--el-input-border-color, var(--el-border-color)) inset;
          height: (--el-input-inner-height);
          flex-wrap: wrap;
          gap: 4px;

          span {
            background-color: var(--light-neutral);
            padding: 2px 8px;
            border-radius: 4px;

          }
        }
      }

    }
  }

  .carded-right {
    margin-top: -8px;

    .w-100 {
      width: 100%;
      display: flex;
      justify-content: flex-end;
      gap: 8px;

      &.draft-line{
        align-items: center;
      }

      .el-button--text {
        color: var(--red);
      }
    }
  }

  .schedule-right {
    display: flex;
    align-items: center;
  }

  .one-flex {
    display: flex;
    align-items: center;
    gap: 8px;

    label {
      font: var(--medium-16);
      color: var(--black-75);
    }

    .status-text {
      flex: 1;
      margin-top: 2px;
    }

  }

  .dialog-footer {
    position: sticky;
    bottom: 0;
    display: flex;
    width: 100%;

    .el-button {
      width: 100%;
    }
  }

  .notify-row{
    display: flex;
    justify-content: end;
  }

  .err-input .el-input__wrapper{
    box-shadow: 0 0 0 1px var(--red, var(--red)) inset;;
  }
}

.save-alert-dialog {
  .dialog-footer {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
  }
}
</style>